import { formatDistanceToNow } from "date-fns";

interface Conversation {
  id: number;
  streamChannelId: string;
  bookingId: number;
  workspaceId: number;
  workspaceTitle: string;
  workspaceImageUrl: string | null;
  otherUserId: number;
  otherUserName: string;
  otherUserImage: string | null;
  lastMessageAt: string | null;
}

interface ConversationListProps {
  conversations: Conversation[];
  selectedId?: number;
  onSelect: (conversation: Conversation) => void;
  isLoading: boolean;
}

export default function ConversationList({
  conversations,
  selectedId,
  onSelect,
  isLoading,
}: ConversationListProps) {
  if (isLoading) {
    return (
      <div className="p-4 space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse flex gap-3">
            <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 text-center text-gray-500">
        <svg
          className="w-12 h-12 mb-3 text-gray-300"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
            d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
          />
        </svg>
        <p className="font-medium">No conversations yet</p>
        <p className="text-sm">Start by booking a workspace!</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-4 border-b border-gray-100">
        <h2 className="font-semibold text-gray-800">Conversations</h2>
      </div>
      <div className="divide-y divide-gray-100">
        {conversations.map((conversation) => (
          <button
            key={conversation.id}
            onClick={() => onSelect(conversation)}
            className={`w-full p-4 flex gap-3 text-left hover:bg-gray-50 transition-colors ${
              selectedId === conversation.id
                ? "bg-blue-50 border-l-4 border-l-[var(--secondary)]"
                : ""
            }`}
          >
            <img
              src={conversation.workspaceImageUrl || "/placeholder-workspace.jpg"}
              alt={conversation.workspaceTitle}
              className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
              onError={(e) => {
                (e.target as HTMLImageElement).src = "/placeholder-workspace.jpg";
              }}
            />
            <div className="flex-1 min-w-0">
              <div className="flex justify-between items-start">
                <p className="font-medium text-gray-900 truncate">
                  {conversation.workspaceTitle}
                </p>
                {conversation.lastMessageAt && (
                  <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                    {formatDistanceToNow(new Date(conversation.lastMessageAt), {
                      addSuffix: true,
                    })}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600 truncate">
                {conversation.otherUserName}
              </p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
